<template>
  <div
    :class="{ 'quick-selection--ranked': ranked }"
    class="quick-selection"
  >
    <div
      class="quick-selection__add"
    >
      <input
        v-model="search"
        :placeholder="placeholder"
        type="text"
        class="form-control quick-selection__input"
        @keyup.esc="search = ''"
        @keyup.down.prevent="moveDown"
        @keyup.up.prevent="moveUp"
        @keydown.enter="addHovered"
        @keydown.tab="addHovered"
        @focus="focus"
        @blur="blur"
        :disabled="disabled"
      >

      <div
        v-show="opened"
        class="quick-selection__add__options"
      >
        <div
          v-for="(option, index) in availableOptions"
          :key="index"
          :class="{ 'quick-selection__add__options__option--hovered': hovering(index) }"
          class="quick-selection__add__options__option"
          @click.stop.prevent="select(option)"
        >
          <slot
            :option="option"
            name="option"
          >
            <span>
              {{ option[searchKey] }}
            </span>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Option from './QuickSelectorOption'

export default {
  components: {
    /* eslint vue/no-unused-components: 0 */
    // actually a bug in eslint
    Option,
  },
  props: {
    allowCreation: {
      required: false,
      default: false,
      type: Boolean,
    },
    searchKey: {
      required: false,
      default: 'name',
      type: String,
    },
    async: {
      required: false,
      default: false,
      type: Boolean,
    },
    options: {
      required: true,
      type: Array,
    },
    multiselect: {
      required: false,
      default: true,
      type: Boolean,
    },
    preSelected: {
      type: Array,
      default () {
        return []
      },
    },
    name: {
      required: false,
      default: 'quick-selection',
      type: String,
    },
    ranked: {
      required: false,
      default: false,
      type: Boolean,
    },
    placeholder: {
      type: String,
      default: 'Add a tag...',
    },
    params: {
      required: false,
      type: Object,
      default () {
        return {}
      },
    },
    component: {
      required: false,
      type: Object,
      default () {
        return this.$options.components.Option
      },
    },

    value: {
      required: true,
      type: Array,
    },

    disabled: {
      default: () => false,
      type: Boolean,
    },
  },

  data () {
    return {
      selected: [],
      availableOptions: [],
      search: '',
      hovered: 0,
      opened: false,
    }
  },

  computed: {
    listener () {
      return this.name + '.sort'
    },
  },

  watch: {
    channelId () {
      this.getEntities()
    },

    search () {
      this.updatedOptions()
      this.$emit('search-changed', this.search)
    },

    options () {
      this.updatedOptions()
    },

    selected () {
      this.$emit('input', this.selected)
    },

    value () {
      this.selected = this.value
    },
  },

  mounted () {
    this.selected = this.value
    this.close()
  },

  methods: {
    inSearch (option) {
      const lower = this.search ? this.search.toLowerCase() : ''

      return this.async || (option[this.searchKey].toLowerCase().indexOf(lower) !== -1 &&
          !this.inSelectedList(option))
    },

    updatedOptions () {
      if (this.search) {
        this.opened = true
      }
      this.availableOptions = []
      this.hovered = 0
      for (const option of this.options) {
        if ((this.inSearch(option))) {
          this.availableOptions.push(option)
        }
      }
      if (this.allowCreation) {
        const creationOption = {
          quickSelectorCreate: true,
        }
        creationOption[this.searchKey] = 'Create ' + (this.search ? this.search : '')
        this.availableOptions.push(creationOption)
      }
    },

    close () {
      this.opened = false
    },

    inSelectedList (check) {
      if (this.selected.indexOf(check) !== -1) {
        return true
      }

      for (const option of this.selected) {
        if (option.id === check.id) {
          return true
        }
      }

      return false
    },

    focus () {
      this.updatedOptions()
      this.opened = true
    },

    blur () {
      setTimeout(this.close, 200)
    },

    moveDown () {
      this.hovered++

      if (this.hovered >= this.availableOptions.length) {
        this.hovered = 0
      }
    },

    moveUp () {
      this.hovered--

      if (this.hovered < 0) {
        this.hovered = this.availableOptions.length - 1
      }
    },

    addHovered (e) {
      if (this.hovered !== -1 && typeof this.availableOptions[this.hovered] !== 'undefined') {
        e.preventDefault()
        e.stopPropagation()
        this.select(this.availableOptions[this.hovered])
      }
    },

    select (option) {
      if (option.quickSelectorCreate) {
        this.$emit('create', this.search)
      } else {
        if (!this.multiselect) {
          this.selected = [option]
        } else {
          this.selected.push(option)
        }
        this.$emit('selection-changed', this.selected)
      }

      this.search = null
      this.close()
    },

    hovering (index) {
      return this.hovered === index
    },
  },
}
</script>

<style lang="scss">
  .quick-selection {
    display: block;

    &.is-invalid {
      input {

        border: 1px solid $red;
        border-radius: $border-radius;
      }
    }

    &--ranked {
      .quick-selection__item {
        &:first-child {
          background-color: $blue;
        }
      }
    }

    &--authors {
      .quick-selection__item {
        &:first-child {
          background: $gray-200;
          color: $gray-700;
        }
      }
    }

    &__input {
      &:focus {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
      &::placeholder {
        color: $input-placeholder-color;
      }
    }

    &__add {
      color: #eaeaea;
      transition: opacity 0.2s ease;
      display: flex;
      position: relative;
      border-radius: 0px 0px 8px 8px;

      &__input {
        flex-grow: 1;
        border: 0;
        border-bottom: 1px solid #eaeaea;
        padding: $input-padding-y $input-padding-x;

        &:focus {
          outline-width: 0;
        }
      }

      &__options {
        max-height: 432px;
        overflow-y: auto;
        top: 38px;
        left: 0;
        width: 100%;
        min-width: 200px;
        border: 1px solid $gray-400;
        border-color: $input-border-color $input-focus-border-color $input-focus-border-color $input-focus-border-color;
        position: absolute;
        background: #fff;
        color: $gray-700;
        z-index: 1988;
        border-radius: 0px 0px 8px 8px;

        &__option {
          padding: $spacer * .5;
          text-align: left;

          &:hover,
          &--hovered {
            background-color: $tellimer-hover;
            color: $tellimer;
            cursor: pointer;
          }
        }
      }
    }

    &:hover {
      .quick-selection__add {
        opacity: 1;
      }
    }

    &__item {
      padding: 0.5rem 0.7rem;
      margin-right: 5px;
      border-radius: 5px;
      border: 1px solid $gray-200;
      background: $gray-100;
      color: $gray-700;
      font-size: 85%;
      line-height: 1.3;

      &:hover {
        border: 1px solid $gray-400;
      }

      &__img {
        width: 25px;
        height: 25px;
        border-radius: 50%;
        border: 1px solid #eaeaea;
      }
    }
  }

</style>
